import React, { useEffect, useState } from "react";
import { CsvFileInput } from "./CsvFileInput";
import { FirstTimeMessageRecipientInput } from "./inputs/FirstTimeMessageRecipientInput";
import { NameInput } from "./inputs/NameInput";
import { FormValues } from "./types";
import { CsvUploadResults } from "./CsvUploadResults";
import {
  processBulkMessageProposal,
  useBulkMessageProposal,
} from "@/hooks/useBulkMessageProposal";
import { MessagingYesNoEnum } from "@/__generated__/graphql";
import { FormState, useFormContext } from "react-hook-form";
import { useOrg } from "@/hooks/useOrg";
import { toast } from "sonner";
import { ApolloError } from "@apollo/client";
import { i18n } from "@/i18n";
import { useBulkMessageProposalCsvLazy } from "@/hooks/useBulkMessageProposalCsv";

interface CsvUploadFormProps {
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
  setCsvFile: React.Dispatch<React.SetStateAction<File | undefined>>;
  csvFile?: File;
  isActive: boolean;
}

export function CsvUploadForm({
  setLoading,
  isLoading,
  setCsvFile,
  csvFile,
  isActive,
}: CsvUploadFormProps) {
  const { formState, watch, setValue } = useFormContext<FormValues>();

  const [firstTimeMessageRecipients = MessagingYesNoEnum.No, userIds = []] =
    watch(["filters.firstTimeMessageRecipients", "filters.userIds"]);

  const [csvError, setCsvError] = useState<ApolloError>();
  const [missingStudentIds, setMissingStudentIds] = useState<number[]>([]);

  const { org } = useOrg();
  const [getBulkMessageProposalCsv] = useBulkMessageProposalCsvLazy();
  const {
    loading: proposalLoading,
    students,
    error,
  } = useBulkMessageIdProposal(userIds, firstTimeMessageRecipients, org?.id);
  useEffect(() => setLoading(proposalLoading), [proposalLoading, setLoading]);

  const onUpload = (file?: File) => {
    if (!org?.id) return;

    setLoading(true);
    setCsvFile(file);
    const fetchUserIds = async () => {
      const { data, error } = await getBulkMessageProposalCsv({
        variables: { orgId: org?.id, csvFile: file },
        fetchPolicy: "no-cache",
      });
      const { students, missingStudentIds } = processBulkMessageProposal(data);
      const ids = students.map(({ id }) => id).filter((id) => id !== undefined);
      setValue("filters.userIds", ids);
      setMissingStudentIds(missingStudentIds ?? []);
      setCsvError(error);
      setLoading(false);
    };
    fetchUserIds();
  };

  showErrors(formState, csvError, error);

  const resetUpload = () => {
    setCsvFile(undefined);
    setValue("filters.userIds", []);
    setMissingStudentIds([]);
    setCsvError(undefined);
  };

  return (
    <div className="flex flex-col gap-4 px-6 pt-3 pb-6">
      <div className="grid grid-cols-2 gap-6">
        <NameInput />
        <FirstTimeMessageRecipientInput disabled={false} />
      </div>
      {!(isLoading || proposalLoading) && (csvFile || userIds.length) ? (
        <CsvUploadResults
          students={students}
          missingIds={missingStudentIds}
          resetUpload={resetUpload}
          csvFile={csvFile}
        />
      ) : (
        <FileInputBox
          onUpload={onUpload}
          loading={isLoading || proposalLoading}
          isActive={isActive}
        />
      )}
    </div>
  );
}

interface FileInputBoxProps {
  onUpload: (file?: File) => void;
  loading: boolean;
  isActive: boolean;
}

function FileInputBox({ onUpload, loading, isActive }: FileInputBoxProps) {
  return (
    <div data-testid="test-file-input" className="flex flex-col gap-4">
      <h4 className="text-sm leading-5 font-medium text-gray-700">
        {i18n.t("bulkMessaging.basicInformation.csvImport.uploadStudents")}
      </h4>
      <CsvFileInput onUpload={onUpload} disabled={isActive} loading={loading} />
    </div>
  );
}

function useBulkMessageIdProposal(
  userIds: string[],
  firstTimeMessageRecipients: MessagingYesNoEnum,
  orgId?: string,
) {
  const testFilters = userIds?.length
    ? {
        userIds,
        firstTimeMessageRecipients: [firstTimeMessageRecipients],
      }
    : {};
  return useBulkMessageProposal({ orgId, filters: testFilters });
}

function showErrors(
  formState: FormState<FormValues>,
  csvError?: ApolloError,
  apiError?: ApolloError,
) {
  if (csvError || apiError) {
    toast.error(
      i18n.t("bulkMessaging.basicInformation.csvImport.failedToUpload.title"),
      {
        description: i18n.t(
          "bulkMessaging.basicInformation.csvImport.failedToUpload.description",
        ),
      },
    );
  }

  if (formState.errors?.filters?.userIds) {
    toast.error(
      i18n.t(
        "bulkMessaging.basicInformation.csvImport.failedToUpload.badCsvFile",
      ),
      { description: formState.errors?.filters?.userIds?.message },
    );
  }
}
